// Pagination Component - Navigate through product pages

import React from 'react';
import {
  Stack,
  IconButton,
  Text,
  Dropdown,
  useTheme,
  mergeStyleSets,
} from '@fluentui/react';
import type { IDropdownOption } from '@fluentui/react';
import type { PaginationInfo } from '../types';

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}) => {
  const theme = useTheme();

  const styles = mergeStyleSets({
    container: {
      padding: '16px',
      backgroundColor: theme.palette.white,
      borderTop: `1px solid ${theme.palette.neutralLight}`,
    },
    pageInfo: {
      fontSize: '14px',
      color: theme.palette.neutralPrimary,
    },
  });

  const pageSizeOptions: IDropdownOption[] = [
    { key: '10', text: '10 per page' },
    { key: '20', text: '20 per page' },
    { key: '30', text: '30 per page' },
    { key: '50', text: '50 per page' },
    { key: '100', text: '100 per page' },
  ];

  const { currentPage, totalPages, totalCount, itemsPerPage } = pagination;
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  const handleFirstPage = () => onPageChange(1);
  const handlePreviousPage = () => onPageChange(currentPage - 1);
  const handleNextPage = () => onPageChange(currentPage + 1);
  const handleLastPage = () => onPageChange(totalPages);

  const handlePageSizeChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      onPageSizeChange(Number(option.key));
    }
  };

  return (
    <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={styles.container}>
      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
        <Text className={styles.pageInfo} role="status" aria-live="polite">
          Showing {startItem} - {endItem} of {totalCount} products
        </Text>
      </Stack>

      <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
        <Dropdown
          options={pageSizeOptions}
          selectedKey={String(itemsPerPage)}
          onChange={handlePageSizeChange}
          ariaLabel="Select page size"
          styles={{ dropdown: { width: 130 } }}
        />

        <Stack horizontal tokens={{ childrenGap: 4 }}>
          <IconButton
            iconProps={{ iconName: 'DoubleChevronLeft' }}
            title="Go to first page"
            ariaLabel={`Go to first page. Currently on page ${currentPage} of ${totalPages}`}
            disabled={currentPage === 1}
            onClick={handleFirstPage}
          />
          <IconButton
            iconProps={{ iconName: 'ChevronLeft' }}
            title="Previous Page"
            ariaLabel="Previous Page"
            disabled={currentPage === 1}
            onClick={handlePreviousPage}
          />
          <Text className={styles.pageInfo} style={{ padding: '0 8px' }}>
            Page {currentPage} of {totalPages}
          </Text>
          <IconButton
            iconProps={{ iconName: 'ChevronRight' }}
            title="Next Page"
            ariaLabel="Next Page"
            disabled={currentPage === totalPages}
            onClick={handleNextPage}
          />
          <IconButton
            iconProps={{ iconName: 'DoubleChevronRight' }}
            title="Last Page"
            ariaLabel="Last Page"
            disabled={currentPage === totalPages}
            onClick={handleLastPage}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
